/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { SagaIterator, Task } from 'redux-saga';
import { AnyAction } from 'redux';
import {
  all,
  call,
  cancel,
  delay,
  fork,
  put,
  race,
  select,
  spawn,
  take,
  TakeEffect,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import {
  Asset,
  Channel,
  Stream,
  Seconds,
  Program,
  ActionRequest,
  ActionSuccess,
  ActionFail,
  XError,
  TrickModes,
  TimeUtils,
  AssetMediaType,
  FileFormat,
  ContentTagsUtils,
  Recording,
  AssetUtils,
  TvAssetUtils,
  RelativeTime,
  ContentProps,
  Movie,
  Episode,
  LicenseOptions,
  PlaybackLicenseData,
  ParentalRatingOptionsType,
  GetPlaybackContextErrors,
  Constants,
  ParentalControlSettings,
  AsyncActionState,
  Ducks,
  AsyncActionStateNoData,
  MenuUiArea,
  MenuItem,
  MenuItemType,
  PlaybackLicenseResult,
  PlaybackLicenseBody,
  BookmarksPhoenixApi,
  ApiContext,
  ConsumptionMeta,
  ConsumptionAction,
  SecuritySessionBookSlot,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import { ActionCreators as ParentalControlActions } from 'core/store/ducks/parentalControl';
import { Selectors as ChannelsSelectors } from 'core/store/ducks/epg/playableChannels';
import { Creators as TunedChannelsCreators } from 'core/store/ducks/epg/tunedChannels';
import { getPlaybackData, getPlaybackLicenseLink } from 'core/store/ducks/playbackLicense';
import { EpgAdjustedData, getEpgPaddedPlayableData } from 'core/store/ducks/epg/padding';
import ParentalControlCore from 'core/services/core/ParentalControl';
import { Selectors as GdprSelectors } from 'core/store/ducks/gdpr';
import { LicenseData, Selectors as SsmSelectors, SagaFunctions as SsmSagaFunctions } from 'core/store/ducks/ssm';
import RailBadgeEngine from 'core/services/core/badgeEngine/RailBadgeEngine';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import { addStartOverActionEvent, addWatchActionEvent } from './analytics/actionsEvent';

const DEFAULT_WATCH_DATA = {
  isTrailerMode: false,
  nextProgram: undefined,
  liveProgress: 0,
  progress: 0,
  paddingStart: 0,
  paddingEnd: 0,
  isPlaybackLocked: false,
  isPlaybackUnlockedByPin: false,
  isPlaybackAdult: false,
  zapping: false,
  isAutoPlayDenied: false,
};

export enum PlaybackState {
  NO_STREAM = 'no_stream',
  STREAM_LOADING = 'stream_loading',
  STREAM_READY = 'stream_ready',
  STREAM_ERROR = 'stream_error',
  BUFFERING = 'buffering',
  PLAY = 'play',
  PLAYING = 'playing',
  PAUSED = 'paused',
  PLAYBACK_COMPLETED = 'playback_completed',
  PLAYBACK_ERROR = 'playback_error',
  STOP = 'stop',
}

export interface WatchRequestParams {
  asset: Asset;
  stream?: Stream;
  progress?: number;
  keepLiveMode?: true;
  zapping?: boolean;
}

export interface SeekRequestParams {
  position: Seconds;
  program?: Program;
}

export interface LicensedStream {
  id: number;
  uri: string;
  token?: string;
  includesPadding?: boolean;
  padded?: boolean;
}

export interface RecommendationsParams {
  upNext: { assets: Asset[]; menuItemId: number } | undefined;
  somethingSimilar: { assets: Asset[]; menuItemId: number } | undefined;
  somethingDifferent: { assets: Asset[]; menuItemId: number } | undefined;
}

export interface RecommendationsItem {
  name: string;
  assets: Asset[];
  menuItemId: number;
}

export enum ActionType {
  SET_PLAYBACK_STATE = 'player/SET_PLAYBACK_STATE',
  SET_PLAYBACK_LOCKED = 'player/SET_PLAYBACK_LOCKED',
  UNLOCK_PLAYBACK_BY_PIN = 'player/UNLOCK_PLAYBACK_BY_PIN',
  SET_CURRENT_ASSET = 'player/SET_CURRENT_ASSET',
  SET_STREAM_PROGRESS = 'player/SET_STREAM_PROGRESS',
  SET_STREAM_DURATION = 'player/SET_STREAM_DURATION',
  SET_RECOMMENDATIONS = 'player/SET_RECOMMENDATIONS',
  SET_RECOMMENDATIONS_ITEM = 'player/SET_RECOMMENDATIONS_ITEM',
  WATCH_REQUEST = 'player/WATCH_REQUEST',
  WATCH_SUCCESS = 'player/WATCH_SUCCESS',
  WATCH_FAIL = 'player/WATCH_FAIL',
  RESTART_REQUEST = 'player/RESTART_REQUEST',
  RESTART_SUCCESS = 'player/RESTART_SUCCESS',
  RESTART_FAIL = 'player/RESTART_FAIL',
  SEEK_REQUEST = 'player/SEEK_REQUEST',
  SEEK_SUCCESS = 'player/SEEK_SUCCESS',
  SEEK_FAIL = 'player/SEEK_FAIL',
  PAUSE_REQUEST = 'player/PAUSE_REQUEST',
  PAUSE_SUCCESS = 'player/PAUSE_SUCCESS',
  PAUSE_FAIL = 'player/PAUSE_FAIL',
  RESUME_REQUEST = 'player/RESUME_REQUEST',
  RESUME_SUCCESS = 'player/RESUME_SUCCESS',
  RESUME_FAIL = 'player/RESUME_FAIL',
  SET_NEXT_PROGRAM = 'player/SET_NEXT_PROGRAM',
  SET_MUTE_STATE = 'player/SET_MUTE_STATE',
  SET_VOLUME = 'player/SET_VOLUME',
  SET_BITRATE = 'player/SET_BITRATE',
  CLEAR_STATE = 'player/CLEAR_STATE',
  LICENSE_REQUEST = 'player/LICENSE_REQUEST',
  SET_AUTO_PLAY_DENIED = 'player/SET_AUTO_PLAY_DENIED',
  ACTIVATE_PLAYER_STATS = 'player/ACTIVATE_PLAYER_STATS',
}

type WatchResult = Partial<ReducerState>;
type SeekResult = Partial<ReducerState>;
type ResumeResult = Partial<ReducerState>;
type AssetUpdateData = Partial<ReducerState> & Pick<ReducerState, 'progress' | 'duration' | 'currentAsset'>;

type WatchRequest = ActionRequest<ActionType.WATCH_REQUEST, WatchRequestParams>;
type WatchSuccess = ActionSuccess<ActionType.WATCH_SUCCESS, WatchResult>;
type WatchFail = ActionFail<ActionType.WATCH_FAIL>;
type RestartRequest = ActionRequest<ActionType.RESTART_REQUEST>;
type RestartSuccess = ActionSuccess<ActionType.RESTART_SUCCESS, WatchResult>;
type RestartFail = ActionFail<ActionType.RESTART_FAIL>;
type SeekRequest = ActionRequest<ActionType.SEEK_REQUEST, SeekRequestParams>;
type SeekSuccess = ActionSuccess<ActionType.SEEK_SUCCESS, SeekResult>;
type SeekFail = ActionFail<ActionType.SEEK_FAIL>;
type PauseRequest = ActionRequest<ActionType.PAUSE_REQUEST>;
type PauseSuccess = ActionSuccess<ActionType.PAUSE_SUCCESS>;
type PauseFail = ActionFail<ActionType.PAUSE_FAIL>;
type ResumeRequest = ActionRequest<ActionType.RESUME_REQUEST>;
type ResumeSuccess = ActionSuccess<ActionType.RESUME_SUCCESS, ResumeResult>;
type ResumeFail = ActionFail<ActionType.RESUME_FAIL>;
type SetPlaybackState = ActionRequest<ActionType.SET_PLAYBACK_STATE, PlaybackState>;
type SetPlaybackLocked = ActionRequest<ActionType.SET_PLAYBACK_LOCKED, boolean>;
type UnlockPlaybackByPin = ActionRequest<ActionType.UNLOCK_PLAYBACK_BY_PIN>;
export type SetCurrentAsset = ActionRequest<ActionType.SET_CURRENT_ASSET, AssetUpdateData>;
type SetStreamProgress = ActionRequest<ActionType.SET_STREAM_PROGRESS, { progress?: number; liveProgress?: number }>;
type SetStreamDuration = ActionRequest<ActionType.SET_STREAM_DURATION, number>;
type SetRecommendations = ActionRequest<ActionType.SET_RECOMMENDATIONS, Asset>;
type SetRecommendationsItem = ActionSuccess<ActionType.SET_RECOMMENDATIONS_ITEM, RecommendationsItem>;
type SetNextProgram = ActionSuccess<ActionType.SET_NEXT_PROGRAM, Program>;
type SetMuteStateRequest = ActionRequest<ActionType.SET_MUTE_STATE, boolean>;
type SetVolume = ActionRequest<ActionType.SET_VOLUME, number>;
type SetBitrate = ActionRequest<ActionType.SET_BITRATE, number>;
type SetAutoPlayDenied = ActionRequest<ActionType.SET_AUTO_PLAY_DENIED, boolean>;
type ClearRequest = ActionRequest<ActionType.CLEAR_STATE>;
type LicenseRequest = ActionRequest<
  ActionType.LICENSE_REQUEST,
  { drmId: string; challenge: string; isRenewal: boolean }
>;
type ActivatePlayerStats = ActionRequest<ActionType.ACTIVATE_PLAYER_STATS, boolean>;

export type PlayerActionTypes =
  | WatchRequest
  | WatchSuccess
  | WatchFail
  | RestartRequest
  | RestartSuccess
  | RestartFail
  | SeekRequest
  | SeekSuccess
  | SeekFail
  | PauseRequest
  | PauseSuccess
  | PauseFail
  | ResumeRequest
  | ResumeSuccess
  | ResumeFail
  | SetPlaybackState
  | SetPlaybackLocked
  | UnlockPlaybackByPin
  | SetCurrentAsset
  | SetStreamProgress
  | SetStreamDuration
  | SetRecommendations
  | SetRecommendationsItem
  | SetMuteStateRequest
  | SetNextProgram
  | SetMuteStateRequest
  | SetVolume
  | SetBitrate
  | SetAutoPlayDenied
  | ClearRequest
  | LicenseRequest
  | ActivatePlayerStats;

const Actions = {
  watchRequest: (meta: WatchRequestParams): WatchRequest => ({
    type: ActionType.WATCH_REQUEST,
    meta,
  }),
  watchSuccess: (payload: WatchResult): WatchSuccess => ({
    type: ActionType.WATCH_SUCCESS,
    payload,
  }),
  watchFail: (error: XError): WatchFail => ({
    type: ActionType.WATCH_FAIL,
    error,
  }),
  restartRequest: (): RestartRequest => ({
    type: ActionType.RESTART_REQUEST,
  }),
  restartSuccess: (payload: WatchResult): RestartSuccess => ({
    type: ActionType.RESTART_SUCCESS,
    payload,
  }),
  restartFail: (error: XError): RestartFail => ({
    type: ActionType.RESTART_FAIL,
    error,
  }),
  seekRequest: (meta: SeekRequestParams): SeekRequest => ({
    type: ActionType.SEEK_REQUEST,
    meta,
  }),
  seekSuccess: (payload: SeekResult): SeekSuccess => ({
    type: ActionType.SEEK_SUCCESS,
    payload,
  }),
  seekFail: (error: XError): SeekFail => ({
    type: ActionType.SEEK_FAIL,
    error,
  }),
  pauseRequest: (): PauseRequest => ({
    type: ActionType.PAUSE_REQUEST,
  }),
  pauseSuccess: (): PauseSuccess => ({
    type: ActionType.PAUSE_SUCCESS,
  }),
  pauseFail: (error: XError): PauseFail => ({
    type: ActionType.PAUSE_FAIL,
    error,
  }),
  resumeRequest: (): ResumeRequest => ({
    type: ActionType.RESUME_REQUEST,
  }),
  resumeSuccess: (payload: ResumeResult): ResumeSuccess => ({
    type: ActionType.RESUME_SUCCESS,
    payload,
  }),
  resumeFail: (error: XError): ResumeFail => ({
    type: ActionType.RESUME_FAIL,
    error,
  }),
  setPlaybackState: (state: PlaybackState): SetPlaybackState => ({
    type: ActionType.SET_PLAYBACK_STATE,
    meta: state,
  }),
  setPlaybackLocked: (isLocked: boolean): SetPlaybackLocked => ({
    type: ActionType.SET_PLAYBACK_LOCKED,
    meta: isLocked,
  }),
  unlockPlaybackByPin: (): UnlockPlaybackByPin => ({
    type: ActionType.UNLOCK_PLAYBACK_BY_PIN,
  }),
  setCurrentAsset: (assetData: AssetUpdateData): SetCurrentAsset => ({
    type: ActionType.SET_CURRENT_ASSET,
    meta: assetData,
  }),
  setStreamProgress: (progress?: Seconds, liveProgress?: Seconds): SetStreamProgress => ({
    type: ActionType.SET_STREAM_PROGRESS,
    meta: {
      progress,
      liveProgress,
    },
  }),
  setStreamDuration: (duration: Seconds): SetStreamDuration => ({
    type: ActionType.SET_STREAM_DURATION,
    meta: duration,
  }),
  setRecommendations: (currentAsset: Asset): SetRecommendations => ({
    type: ActionType.SET_RECOMMENDATIONS,
    meta: currentAsset,
  }),
  setRecommendationsItem: (item: RecommendationsItem): SetRecommendationsItem => ({
    type: ActionType.SET_RECOMMENDATIONS_ITEM,
    payload: item,
  }),
  setMuteState: (mute: boolean): SetMuteStateRequest => ({
    type: ActionType.SET_MUTE_STATE,
    meta: mute,
  }),
  setVolume: (volume: number): SetVolume => ({
    type: ActionType.SET_VOLUME,
    meta: volume,
  }),
  setBitrate: (bitrate: number): SetBitrate => ({
    type: ActionType.SET_BITRATE,
    meta: bitrate,
  }),
  setNextProgram: (nextProgram: Program): SetNextProgram => ({
    type: ActionType.SET_NEXT_PROGRAM,
    payload: nextProgram,
  }),
  setAutoPlayDenied: (autoPlayDenied: boolean): SetAutoPlayDenied => ({
    type: ActionType.SET_AUTO_PLAY_DENIED,
    meta: autoPlayDenied,
  }),
  clearState: (): ClearRequest => ({
    type: ActionType.CLEAR_STATE,
  }),
  licenseRequest: (drmId: string, challenge: string, isRenewal: boolean): LicenseRequest => ({
    type: ActionType.LICENSE_REQUEST,
    meta: { drmId, challenge, isRenewal },
  }),
  activatePlayerStats: (activate: boolean): ActivatePlayerStats => ({
    type: ActionType.ACTIVATE_PLAYER_STATS,
    meta: activate,
  }),
};

export const ActionCreators = {
  watchRequest: Actions.watchRequest,
  restartRequest: Actions.restartRequest,
  seekRequest: Actions.seekRequest,
  pauseRequest: Actions.pauseRequest,
  resumeRequest: Actions.resumeRequest,
  setPlaybackState: Actions.setPlaybackState,
  setPlaybackLocked: Actions.setPlaybackLocked,
  unlockPlaybackByPin: Actions.unlockPlaybackByPin,
  setStreamProgress: Actions.setStreamProgress,
  setStreamDuration: Actions.setStreamDuration, // value in miliseconds
  setRecommendations: Actions.setRecommendations,
  setRecommendationsItem: Actions.setRecommendationsItem,
  setNextProgram: Actions.setNextProgram,
  setMuteState: Actions.setMuteState,
  setVolume: Actions.setVolume,
  setBitrate: Actions.setBitrate,
  setAutoPlayDenied: Actions.setAutoPlayDenied,
  clearState: Actions.clearState,
  licenseRequest: Actions.licenseRequest,
  activatePlayerStats: Actions.activatePlayerStats,
};

// SELECTORS
export const Selectors = {
  data: (state: StoreState): ReducerState => state.player,
  watchRequest: (state: StoreState): AsyncActionState<WatchRequestParams | undefined> | undefined => state.player.watch,
  zapping: (state: StoreState): boolean => state.player.zapping,
  trickModesEnable: (state: StoreState): boolean => state.player.trickModesEnable,
  trickModes: (state: StoreState): TrickModes | undefined => state.player.trickModes,
  isTrailerMode: (state: StoreState): boolean => state.player.isTrailerMode,
  isLiveMode: (state: StoreState): boolean => state.player.isLiveMode,
  isLivePosition: (state: StoreState): boolean | undefined => state.player.isLivePosition,
  isLive: (state: StoreState): boolean => !!state.player.isLivePosition, // For an Asset to be live isLivePosition need to be true
  isWatchProcessing: (state: StoreState): boolean => state.player.watch?.processing || false,
  isWatchProcessed: (state: StoreState): boolean => state.player.watch?.processed || false,
  watchError: (state: StoreState): XError | undefined => state.player.watch?.error,
  isRestartProcessing: (state: StoreState): boolean => state.player.restart?.processing || false,
  isRestartProcessed: (state: StoreState): boolean => state.player.restart?.processed || false,
  restartError: (state: StoreState): XError | undefined => state.player.restart?.error,
  isSeekProcessed: (state: StoreState): boolean => state.player.seek?.processed || false,
  isSeekProcessing: (state: StoreState): boolean => state.player.seek?.processing || false,
  seekError: (state: StoreState): XError | undefined => state.player.seek?.error,
  isPauseProcessed: (state: StoreState): boolean => state.player.pause?.processed || false,
  pauseError: (state: StoreState): XError | undefined => state.player.pause?.error,
  isMute: (state: StoreState): boolean => state.player.isMute,
  volume: (state: StoreState): number => state.player.volume,
  bitrate: (state: StoreState): number => state.player.bitrate,
  isResumeProcessed: (state: StoreState): boolean => state.player.resume?.processed || false,
  isResumeProcessing: (state: StoreState): boolean => state.player.resume?.processing || false,
  resumeError: (state: StoreState): XError | undefined => state.player.resume?.error,
  playbackState: (state: StoreState): PlaybackState => state.player.playbackState,
  currentAsset: (state: StoreState): Asset | undefined => state.player.currentAsset,
  currentStream: (state: StoreState): LicensedStream | undefined => state.player.currentStream,
  targetStreamPosition: (state: StoreState): Seconds | undefined => state.player.targetStreamPosition,
  originalStream: (state: StoreState): Stream | undefined => state.player.originalStream,
  channel: (state: StoreState): Channel | undefined => state.player.relatedChannel,
  currentDuration: (state: StoreState): Seconds => state.player.duration,
  currentProgress: (state: StoreState): Seconds => state.player.progress,
  liveProgress: (state: StoreState): Seconds => state.player.liveProgress,
  recommendations: (state: StoreState): RecommendationsParams => state.player.recommendations,
  paddingStart: (state: StoreState): Seconds => state.player.paddingStart || 0,
  paddingEnd: (state: StoreState): Seconds => state.player.paddingEnd || 0,
  nextProgram: (state: StoreState): Program | undefined => state.player.nextProgram,
  isPlaybackLocked: (state: StoreState): boolean => state.player.isPlaybackLocked,
  isPlaybackUnlockedByPin: (state: StoreState): boolean => state.player.isPlaybackUnlockedByPin,
  isPlaybackAdult: (state: StoreState): boolean => state.player.isPlaybackAdult,
  // SSM proxy selectors
  ssmLicense: (state: StoreState): LicenseData | undefined => SsmSelectors.license(state),
  ssmError: (state: StoreState): XError | undefined => SsmSelectors.error(state),
  isAutoPlayDenied: (state: StoreState): boolean => state.player.isAutoPlayDenied,
  isPlayerStatsActivated: (state: StoreState): boolean | undefined => state.player.playerStats,
};

// SSM

// the body for the license link and for the checkEntitlements is the same
// so we save it here for use in the second request
let licenseLinkRequestBody: PlaybackLicenseBody | undefined;
let curConsumptionData: ConsumptionMeta; // contextual data used for the bookmarks reporting
// tasks created when a new ssm session is created
let keepSessionTask: Task | undefined;
let stopSessionTask: Task | undefined;

function* cancelKeepSessionTask(): SagaIterator {
  if (keepSessionTask) {
    yield cancel(keepSessionTask);
    keepSessionTask = undefined;
  }
}

function* cancelStopSessionTask(): SagaIterator {
  if (stopSessionTask) {
    yield cancel(stopSessionTask);
    stopSessionTask = undefined;
  }
}

function getConsumptionActionPlayType(isLive: boolean, startPlayPosition: Seconds): ConsumptionAction {
  let consumptionAction = ConsumptionAction.PLAY;

  if (!isLive && startPlayPosition === 0) {
    consumptionAction = ConsumptionAction.FIRST_PLAY;
  }

  // NOTE: the specs states that startPlayPosition of 95% shall be considered as no progress
  // we are not checking it here because the watch options rules already do it

  return consumptionAction;
}

function getConsumptionData(newPlayableData: WatchResult): ConsumptionMeta {
  return {
    isLive: !!newPlayableData.isLivePosition,
    asset: newPlayableData.currentAsset,
    channel: newPlayableData.relatedChannel,
    fileId: newPlayableData.originalStream?.id || 0, // there is no chance the stream be undefined because there is a validation in the watch request
    location: newPlayableData.progress || 0,
  };
}

function ssmSessionProgramRollover(asset: Asset | undefined) {
  if (curConsumptionData) {
    // curConsumptionData is defined when ssm is enable
    curConsumptionData.asset = asset;
  }
}

function* getSetBookmarkBody(
  consumptionMeta: ConsumptionMeta,
  consumptionAction: ConsumptionAction
): SagaIterator<Record<string, unknown>> {
  const { isLive, asset, channel, fileId, location } = consumptionMeta;
  let bookmarkAsset = asset;
  let programId: number | undefined;
  if (isLive) {
    bookmarkAsset = channel;
    programId = asset?.id;
  }
  const apiCtx: ApiContext = yield select(Ducks.ApiContextSelector.default);
  return BookmarksPhoenixApi.getSetBookmarkBody(
    apiCtx,
    bookmarkAsset,
    fileId,
    consumptionAction,
    Math.floor(location),
    programId
  );
}

function* keepSsmSession(consumptionData: ConsumptionMeta, consumptionAction: ConsumptionAction): SagaIterator {
  const setBookmarkBody = yield call(getSetBookmarkBody, consumptionData, consumptionAction);

  // renew is used for both: player requests a license renewal and keep session request
  yield call(SsmSagaFunctions.renewLicense, setBookmarkBody);
}

function* stopSsmSession(): SagaIterator {
  const location: Seconds = yield select(Selectors.currentProgress);

  const consumptionData = { ...curConsumptionData, location };
  const setBookmarkBody = yield call(getSetBookmarkBody, consumptionData, ConsumptionAction.STOP);

  if (consumptionData.asset) {
    // LOCAL CACHE UPDATE: the last session bookmark is saved in the local cache
    yield fork(Ducks.BookmarksCache.updateBookmarksSaga, consumptionData.asset, location);
  }

  yield call(SsmSagaFunctions.stopSession, setBookmarkBody);
}

// executed in clear streams and legacy mode
function* KeepSsmSession(heartbeat: Seconds): SagaIterator {
  while (true) {
    yield delay(TimeUtils.secondsToMilliseconds(heartbeat));

    const progress: Seconds = yield select(Selectors.currentProgress);
    const state = yield select(Selectors.playbackState);
    yield call(
      keepSsmSession,
      { ...curConsumptionData, location: progress || 0 },
      state === PlaybackState.PAUSED ? ConsumptionAction.PAUSE : ConsumptionAction.HIT
    );
  }
}

function* handleKeepSession(heartbeat: Seconds): SagaIterator {
  try {
    const task = yield fork(KeepSsmSession, heartbeat);
    // SSM enforced: if player requests a license it means that it is not a clear or legacy session
    yield take(ActionType.LICENSE_REQUEST);
    yield cancel(task); // the keep session task will be aborted
  } finally {
    keepSessionTask = undefined;
  }
}

function* handleSessionStop(): SagaIterator {
  try {
    yield take<SetPlaybackState>(
      (act: AnyAction) =>
        (act.type === ActionType.SET_PLAYBACK_STATE &&
          (act.meta === PlaybackState.STOP || act.meta === PlaybackState.PLAYBACK_ERROR)) ||
        act.type === Ducks.Credentials.ActionTypes.LOGOUT_REQUEST
    );
    yield call(cancelKeepSessionTask);
    yield call(stopSsmSession);
  } finally {
    stopSessionTask = undefined;
  }
}

// if there is a previous session:
// -closes the session in the GTW and cancel the "child" tasks
function* handlePreviousSession(): SagaIterator {
  const isValidSession = yield select(SsmSelectors.isValidSession);
  if (isValidSession) {
    yield call(stopSsmSession);
    yield call(cancelKeepSessionTask);
    yield call(cancelStopSessionTask);
  }
}

function* startSsmSession(
  consumptionData: ConsumptionMeta,
  consumptionAction: ConsumptionAction,
  isTrailerMode?: boolean
): SagaIterator {
  const isSsmEnabled: boolean = yield select(Ducks.Dms.Selectors.isSsmEnabled);

  if (!isSsmEnabled) {
    return;
  }

  yield call(handlePreviousSession);

  if (isTrailerMode) {
    return;
  }

  const setBookmarkBody = yield call(getSetBookmarkBody, consumptionData, consumptionAction);

  const resSession: SecuritySessionBookSlot = yield call(SsmSagaFunctions.startSession, setBookmarkBody);
  curConsumptionData = consumptionData;

  keepSessionTask = yield spawn(handleKeepSession, resSession.heartbeat);
  stopSessionTask = yield spawn(handleSessionStop);
}

function* startSsmSessionWatch(playableData: WatchResult): SagaIterator {
  const consumptionData = getConsumptionData(playableData);
  const action = getConsumptionActionPlayType(consumptionData.isLive, consumptionData.location);

  yield call(startSsmSession, consumptionData, action, playableData.isTrailerMode);
}

function* startSsmSessionLiveSeek(isLive: boolean, seekPosition: Seconds): SagaIterator {
  const consumptionData: ConsumptionMeta = {
    ...curConsumptionData,
    isLive,
    location: seekPosition,
  };
  yield call(startSsmSession, consumptionData, ConsumptionAction.PLAY);
}

function* startSsmSessionLiveResume(resumePosition: Seconds): SagaIterator {
  const consumptionData: ConsumptionMeta = { ...curConsumptionData, isLive: false, location: resumePosition };
  yield call(startSsmSession, consumptionData, ConsumptionAction.PLAY);
}

function* ssmLicenseRequestSaga(action: LicenseRequest): SagaIterator {
  const licenseParams = {
    drmId: action.meta.drmId,
    challenge: action.meta.challenge,
  };

  const progress: Seconds = yield select(Selectors.currentProgress);

  const consumptionData: ConsumptionMeta = { ...curConsumptionData, location: progress || 0 };

  const state = yield select(Selectors.playbackState);

  if (action.meta.isRenewal) {
    const setBookmarkBody = yield call(
      getSetBookmarkBody,
      consumptionData,
      state === PlaybackState.PAUSED ? ConsumptionAction.PAUSE : ConsumptionAction.HIT
    );
    yield call(SsmSagaFunctions.renewLicense, setBookmarkBody, licenseParams);
  } else {
    const consumptionAction = getConsumptionActionPlayType(consumptionData.isLive, consumptionData.location);
    const setBookmarkBody = yield call(getSetBookmarkBody, consumptionData, consumptionAction);
    yield call(SsmSagaFunctions.getLicense, licenseLinkRequestBody || {}, setBookmarkBody, licenseParams);

    licenseLinkRequestBody = undefined;
  }
}

// SSM END

function* loadNextProgram(channelEpgId: number, currentProgram: Program): SagaIterator {
  const nextPrograms: Program[] | undefined = yield call(
    Ducks.Epg.Programs.getFuturePrograms,
    channelEpgId,
    currentProgram,
    1
  );

  if (nextPrograms?.length) {
    yield put(Actions.setNextProgram(nextPrograms[0]));
  }
}

function getTvStream(streams: Stream[], mediaType: AssetMediaType): Stream {
  // for recordings select the first stream where the fileFormat is NPVR
  if (mediaType === AssetMediaType.RECORDING) {
    return streams.find((s) => s.fileFormat === FileFormat.NPVR) || streams[0];
  }

  return streams[0];
}

function* getLivePlayableData(params: WatchRequestParams): SagaIterator<WatchResult> {
  const channel = params.asset as Channel;

  // get the current program
  const currentProgram: Program | undefined = yield call(Ducks.Epg.Live.getLiveProgram, channel.epgId);

  // TODO: é obrigatório existir sempre program, principalmente por causa de parental control
  // pode ser necessário criar dummy programs quando n há programação do canal

  let trickModesEnable = false;
  let trickModes: TrickModes | undefined;
  if (currentProgram) {
    trickModesEnable = ContentTagsUtils.isResartAllowedForLiveAsset(currentProgram);
    const trickModesEpg: TrickModes[] = ContentTagsUtils.mapContentTagsToTrickModes(currentProgram);
    [trickModes] = trickModesEpg;
  }

  return {
    isLiveMode: true,
    trickModesEnable,
    trickModes,
    isLivePosition: true,
    currentAsset: currentProgram,
    relatedChannel: channel,
    originalStream: getTvStream(channel.streams, channel.mediaType),
    targetStreamPosition: undefined, // default stream position
    progress: currentProgram ? TimeUtils.nowUnixEpoch() - TimeUtils.toUnixEpoch(currentProgram.startDate) : 0,
    duration: currentProgram ? (currentProgram.durationValue as number) * TimeUtils.MIN_SECS : 0,
  };
}

function* getTvNonLivePlayableData(params: WatchRequestParams): SagaIterator<WatchResult> {
  const asset = params.asset as Program | Recording;
  const relatedChannel: Channel | undefined = yield select(ChannelsSelectors.getChannelByEpgId, asset.channelEpgId);
  if (!relatedChannel) {
    throw XError.fromError(new Error(`Trying to watch content in a not playable channel: ${asset.id}`));
  }
  const duration = (asset.durationValue as number) * TimeUtils.MIN_SECS;

  let trickModesEnable = false;
  let trickModes: TrickModes | undefined;

  const trickModesEpg: TrickModes[] = ContentTagsUtils.mapContentTagsToTrickModes(asset);
  if (AssetUtils.isEpg(asset)) {
    trickModesEnable = ContentTagsUtils.isResartAllowedForCatchupAsset(asset);
    [, trickModes] = trickModesEpg;
  } else {
    trickModesEnable = ContentTagsUtils.isResartAllowedForRecording(asset);
    [, , trickModes] = trickModesEpg;
  }

  const targetStreamPosition =
    params.progress !== undefined ? AssetUtils.getProgressInSeconds(params.progress, duration) : undefined;

  return {
    isLiveMode: TvAssetUtils.getRelativeTime(asset) === RelativeTime.LIVE || params.keepLiveMode,
    isLivePosition: false,
    trickModesEnable,
    trickModes,
    currentAsset: asset,
    relatedChannel,
    originalStream: getTvStream(relatedChannel.streams, asset.mediaType),
    targetStreamPosition,
    progress: targetStreamPosition || 0,
    duration,
  };
}

function getVodPlayableData(params: WatchRequestParams): WatchResult {
  const isTrailer = params?.stream?.fileFormat === FileFormat.TRAILER;
  const duration: Seconds = !isTrailer
    ? ((params.asset as ContentProps).durationValue as number) * TimeUtils.MIN_SECS
    : 0;
  const targetStreamPosition =
    params.progress !== undefined ? AssetUtils.getProgressInSeconds(params.progress, duration) : undefined;
  return {
    isTrailerMode: isTrailer,
    isLiveMode: false,
    isLivePosition: undefined,
    trickModesEnable: ContentTagsUtils.isResartAllowedForVodAsset(params.asset as Movie | Episode),
    trickModes: ContentTagsUtils.mapContentTagsToTrickModes(params.asset as Movie | Episode)[0],
    currentAsset: params.asset,
    relatedChannel: undefined,
    originalStream: params.stream,
    targetStreamPosition,
    progress: targetStreamPosition || 0,
    duration,
  };
}

function* getAssetLicensedStream(
  asset: Asset,
  originalStream: Stream,
  options?: LicenseOptions
): SagaIterator<LicensedStream> {
  if (originalStream.fileFormat === FileFormat.TRAILER) {
    // trailers are ready to use streams, so there is no license request
    return {
      uri: originalStream.url,
      id: originalStream.id,
    };
  }

  const isSsmEnabled: boolean = yield select(Ducks.Dms.Selectors.isSsmEnabled);

  if (!isSsmEnabled) {
    const playbackLicenseData: PlaybackLicenseData = yield call(getPlaybackData, asset, originalStream, options);
    return {
      uri: playbackLicenseData.url,
      token: playbackLicenseData.token,
      id: originalStream.id,
    };
  }

  const playbackLicenseResult: PlaybackLicenseResult = yield call(
    getPlaybackLicenseLink,
    asset,
    originalStream,
    options
  );

  licenseLinkRequestBody = playbackLicenseResult.requestBody;

  return {
    uri: playbackLicenseResult.data.url,
    id: originalStream.id,
  };
}

function* isSameContent(asset: Asset, stream?: Stream): SagaIterator<boolean> {
  const linear: boolean = yield select(Selectors.isLive);
  const channel: Channel = yield select(Selectors.channel);
  const currentAsset: Asset | undefined = yield select(Selectors.currentAsset);
  const currentStream: Stream | undefined = yield select(Selectors.originalStream);

  const tuningSameChannel = linear && AssetUtils.isChannel(asset) && (channel === asset || channel.id === asset.id);
  const tuningSameAsset = asset.id === currentAsset?.id;
  const tuningSameAssetAndSameStream = tuningSameAsset && (!stream || stream.id === currentStream?.id); // check the stream (if defined)
  return tuningSameChannel || tuningSameAssetAndSameStream;
}

function* applyPaddingToEpg(
  resultData: Partial<ReducerState>,
  stream: LicensedStream,
  program: Program
): SagaIterator<Partial<ReducerState>> {
  const epgAdjustedData: EpgAdjustedData | undefined = yield call(getEpgPaddedPlayableData, program, stream);
  // only apply for catchup events

  const res: Partial<ReducerState> = {
    ...resultData,
    ...epgAdjustedData, // if there is a adjusted stream, currentStream, paddingStart, paddingEnd and duration properties will be replaced here
  };

  if (res.currentStream) {
    res.currentStream = { ...res.currentStream, padded: true };
  }

  return res;
}
/**
 * actions:
 * -play: movie, episode, program/EPG (past), recording, channel (live)
 * -restart: movie, episode, program/EPG (past), recording, current program (live)
 */
function* watchRequestSaga(action: WatchRequest): SagaIterator {
  const { meta: playRequestParams } = action;
  let playableData: WatchResult | undefined;
  try {
    if (yield call(isSameContent, playRequestParams.asset, playRequestParams.stream)) {
      yield put(Actions.watchSuccess({})); // set the action success but without changes
      return;
    }

    switch (playRequestParams.asset.mediaType) {
      case AssetMediaType.LINEAR:
        yield put(TunedChannelsCreators.Update(playRequestParams.asset as Channel));
        playableData = yield call(getLivePlayableData, playRequestParams);
        break;
      case AssetMediaType.EPG:
      case AssetMediaType.RECORDING:
        playableData = yield call(getTvNonLivePlayableData, playRequestParams);
        break;
      case AssetMediaType.MOVIE:
      case AssetMediaType.EPISODE:
        playableData = getVodPlayableData(playRequestParams);
        break;
      default:
        throw XError.fromError(new Error(`Unexpected Asset type: ${playRequestParams.asset.mediaType}`));
    }

    playableData = { ...DEFAULT_WATCH_DATA, ...playableData };

    if (!playableData.originalStream) {
      throw XError.fromError(new Error(`Unexpected missing stream for asset: ${playRequestParams.asset.id}`));
    }

    yield call(startSsmSessionWatch, playableData);

    const { currentAsset, isLivePosition } = playableData;
    // only apply for catchup events
    const applyPadding = !!(!isLivePosition && currentAsset && AssetUtils.isEpg(currentAsset));

    const options: LicenseOptions = {
      applyPadding,
    };
    const currentStream: LicensedStream = yield call(
      getAssetLicensedStream,
      playRequestParams.asset,
      playableData.originalStream,
      options
    );

    const assetParentalState = currentAsset ? ParentalControlCore.UI.getAssetParentalState(currentAsset) : undefined;

    const isPlaybackLocked = assetParentalState === ParentalRatingOptionsType.PIN;
    const isPlaybackAdult = currentAsset ? ParentalControlCore.UI.isAdult(currentAsset) : undefined;

    const { zapping } = playRequestParams;

    let watchResult = {
      ...playableData,
      currentStream,
      isPlaybackLocked,
      isPlaybackAdult,
      zapping,
      playbackState: PlaybackState.STREAM_LOADING,
    };

    if (applyPadding) {
      watchResult = yield call(applyPaddingToEpg, watchResult, currentStream, currentAsset as Program);
      const { paddingStart, targetStreamPosition } = watchResult;
      if (targetStreamPosition === undefined && paddingStart !== 0) {
        watchResult = {
          ...watchResult,
          targetStreamPosition: paddingStart,
        };
      }
    }

    yield put(Actions.watchSuccess(watchResult));

    // in live only, the next prog may be used for:
    // -next playing asset after a start over ends
    // -next playing asset after the current live ends
    if (watchResult.isLiveMode && watchResult.relatedChannel && watchResult.currentAsset) {
      yield call(loadNextProgram, watchResult.relatedChannel.epgId, watchResult.currentAsset as Program);
    }
    if (watchResult.currentAsset && watchResult.originalStream) {
      yield fork(addWatchActionEvent, watchResult.currentAsset, watchResult.originalStream, watchResult.isLiveMode);
    }
  } catch (e) {
    const error = XError.fromError(e, GetPlaybackContextErrors.generic.unknownError);
    yield put(Actions.watchFail(error));
    if (playableData && playableData.currentAsset && playableData.originalStream) {
      yield fork(
        addWatchActionEvent,
        playableData.currentAsset,
        playableData.originalStream,
        playableData.isLiveMode,
        error
      );
    }
  }
}

function* getNonLiveLicensedStream(originalStream: Stream, options?: LicenseOptions): SagaIterator<LicensedStream> {
  const currentAsset: Asset = yield select(Selectors.currentAsset);

  const mergedOptions = AssetUtils.isEpg(currentAsset)
    ? {
        streamStartTime: TimeUtils.toIsoStringSeconds(currentAsset.startDate),
        streamEndTime: TimeUtils.toIsoStringSeconds(currentAsset.endDate),
        ...options,
      }
    : options;

  return yield call(getAssetLicensedStream, currentAsset, originalStream, mergedOptions);
}

function* getLiveLicensedStream(originalStream: Stream): SagaIterator<LicensedStream> {
  const currentChannel: Asset = yield select(Selectors.channel);

  // Do not apply padding to live streams
  return yield call(getAssetLicensedStream, currentChannel, originalStream);
}

function* seek(params: SeekRequestParams, isRestart?: boolean): SagaIterator<SeekResult> {
  let seekResult: SeekResult = {};

  const playbackState: PlaybackState = yield select(Selectors.playbackState);
  if (
    playbackState !== PlaybackState.PLAYING &&
    playbackState !== PlaybackState.PAUSED &&
    playbackState !== PlaybackState.BUFFERING &&
    playbackState !== PlaybackState.PLAYBACK_COMPLETED
  ) {
    return seekResult; // dont change state
  }

  let targetStreamPosition: Seconds | undefined = params.position;

  const isLiveMode: boolean = yield select(Selectors.isLiveMode);
  const isPLayingLiveStream: boolean = yield select(Selectors.isLivePosition);
  const currentLicensedStream: LicensedStream = yield select(Selectors.currentStream);
  const currentAsset: Asset | undefined = yield select(Selectors.currentAsset);
  const currentPaddingStart: Seconds = yield select(Selectors.paddingStart);

  // handling stream transitions cases
  const nonLiveToLiveSwitching =
    isLiveMode && !isPLayingLiveStream && targetStreamPosition === Constants.LIVE_POSITION_VALUE;
  // non live transitions
  const liveToNonLiveSwitching =
    isLiveMode && isPLayingLiveStream && targetStreamPosition !== Constants.LIVE_POSITION_VALUE;
  const nonLiveToNonLivePadding = isLiveMode && !isPLayingLiveStream && isRestart && !currentLicensedStream.padded;

  // if it is a transition case
  if (liveToNonLiveSwitching || nonLiveToLiveSwitching || nonLiveToNonLivePadding) {
    let originalStream: Stream = yield select(Selectors.originalStream);

    // when switching from ongoing recording to the live we must set the "normal" stream instead of a NPVR
    if (nonLiveToLiveSwitching) {
      if (currentAsset && AssetUtils.isRecording(currentAsset)) {
        const channel: Channel = yield select(Selectors.channel);
        originalStream = getTvStream(channel.streams, channel.mediaType);
      }
    }

    const licensedStreamMethod = nonLiveToLiveSwitching ? getLiveLicensedStream : getNonLiveLicensedStream;

    // only apply padding when it is a restart and:
    // -the current stream is live (switching from live to catchup) or
    // -the current stream is already catchup but without padding (when user seek but without restart)
    const applyPadding = !!((liveToNonLiveSwitching || nonLiveToNonLivePadding) && isRestart);

    const options: LicenseOptions = {
      applyPadding,
    };

    let progress = targetStreamPosition;
    const isLivePosition = targetStreamPosition === Constants.LIVE_POSITION_VALUE; // new live position value

    // switching to live
    if (isLivePosition) {
      targetStreamPosition = undefined; // don't specifiy any target position because it will be the default live as soon as the stream is open
      progress = yield select(Selectors.liveProgress); // just for UI while the stream is opennig
    }

    yield call(startSsmSessionLiveSeek, isLivePosition, progress);

    const currentStream: LicensedStream = yield call(licensedStreamMethod, originalStream, options);

    seekResult = {
      isLivePosition,
      originalStream,
      currentStream, // new stream
      targetStreamPosition,
      playbackState: PlaybackState.STREAM_LOADING,
      progress,
    };

    if (applyPadding) {
      seekResult = yield call(applyPaddingToEpg, seekResult, currentStream, currentAsset as Program);
      const { paddingStart } = seekResult;
      if (targetStreamPosition === 0 && paddingStart !== undefined && paddingStart !== 0) {
        seekResult = {
          ...seekResult,
          targetStreamPosition: paddingStart,
        };
      }
    } else if (currentPaddingStart) {
      // remove paddings from previous state if they exist
      seekResult = {
        ...seekResult,
        paddingStart: 0,
        paddingEnd: 0,
      };
    }
  } else {
    // for the remaining scenarios just confirms the operation success with the requested targetPosition
    // however if it is a restart the targetPosition will be the padding start value
    const adaptedTargetPos = isRestart && currentPaddingStart ? currentPaddingStart : targetStreamPosition;
    seekResult = {
      targetStreamPosition: adaptedTargetPos,
      progress: adaptedTargetPos,
    };
  }

  return seekResult;
}

/**
 * actions:
 * EPG(live or past), MOVIE, EPISODE, RECORDING
 */
function* restartRequestSaga(): SagaIterator {
  const currentAsset: Asset | undefined = yield select(Selectors.currentAsset);
  const originalStream: Stream | undefined = yield select(Selectors.originalStream);

  try {
    const result: SeekResult = yield call(seek, { position: 0 }, true);
    yield put(Actions.restartSuccess(result));

    if (currentAsset && originalStream) {
      yield fork(addStartOverActionEvent, currentAsset, originalStream);
    }
  } catch (e) {
    const error = XError.fromError(e, GetPlaybackContextErrors.generic.unknownError);
    yield put(Actions.restartFail(error));
    if (currentAsset && originalStream) {
      yield fork(addStartOverActionEvent, currentAsset, originalStream, error);
    }
  }
}

/**
 * actions:
 * EPG(live or past), MOVIE, EPISODE, RECORDING
 */
function* seekRequestSaga(action: SeekRequest): SagaIterator {
  try {
    const result: SeekResult = yield call(seek, action.meta);
    yield put(Actions.seekSuccess(result));
  } catch (error) {
    yield put(Actions.seekFail(XError.fromError(error, GetPlaybackContextErrors.generic.unknownError)));
  }
}

function* pauseRequestSaga(): SagaIterator {
  const playbackState: PlaybackState = yield select(Selectors.playbackState);

  if (playbackState !== PlaybackState.PLAYING) {
    return;
  }

  yield put(Actions.pauseSuccess());
}

function* resumeRequestSaga(): SagaIterator {
  try {
    const playbackState: PlaybackState = yield select(Selectors.playbackState);
    const isAutoPlayDenied: boolean = yield select(Selectors.isAutoPlayDenied);

    if (playbackState !== PlaybackState.PAUSED) {
      return;
    }

    const isLiveMode: boolean = yield select(Selectors.isLiveMode);
    const isPLayingLiveStream: boolean = yield select(Selectors.isLivePosition);
    const currentAsset: Asset = yield select(Selectors.currentAsset);
    const assetParentalState = currentAsset ? ParentalControlCore.UI.getAssetParentalState(currentAsset) : undefined;
    const isPlaybackLocked = assetParentalState === ParentalRatingOptionsType.PIN;
    const isPlaybackAdult = currentAsset ? ParentalControlCore.UI.isAdult(currentAsset) : undefined;

    if (isLiveMode && isPLayingLiveStream && !isAutoPlayDenied) {
      const originalStream: Stream = yield select(Selectors.originalStream);
      const resumePosition = yield select(Selectors.currentProgress);
      // start a new session when switching from live to catchup
      yield call(startSsmSessionLiveResume, resumePosition);

      // Do not apply padding on resume
      const currentStream: LicensedStream = yield call(getNonLiveLicensedStream, originalStream);
      yield put(
        Actions.resumeSuccess({
          isLivePosition: false, // it will play non live position
          currentStream, // new stream
          targetStreamPosition: resumePosition,
          playbackState: PlaybackState.STREAM_LOADING,
          isPlaybackLocked,
          isPlaybackAdult,
        })
      );

      return;
    }

    yield put(Actions.resumeSuccess({ targetStreamPosition: undefined, isPlaybackLocked, isPlaybackAdult }));
  } catch (error) {
    yield put(Actions.resumeFail(XError.fromError(error, GetPlaybackContextErrors.generic.unknownError)));
  }
}

function* liveRollover(): SagaIterator {
  // by default nextProgram state contains the new live program
  const newLiveProgram: Program | undefined = yield select(Selectors.nextProgram);
  const assetParentalState = newLiveProgram ? ParentalControlCore.UI.getAssetParentalState(newLiveProgram) : undefined;
  const isPlaybackLocked = assetParentalState === ParentalRatingOptionsType.PIN;
  const isPlaybackAdult = newLiveProgram ? ParentalControlCore.UI.isAdult(newLiveProgram) : undefined;

  ssmSessionProgramRollover(newLiveProgram);

  yield put(
    Actions.setCurrentAsset({
      currentAsset: newLiveProgram,
      duration: TimeUtils.minutesToSeconds(newLiveProgram?.durationValue || 0),
      progress: 0,
      isPlaybackLocked,
      isPlaybackUnlockedByPin: false,
      isPlaybackAdult,
    })
  );

  if (newLiveProgram) {
    // fetch the next program after live to be used later
    yield call(loadNextProgram, newLiveProgram.channelEpgId, newLiveProgram);
  }
}

function* progressUpdatedSaga(action: SetStreamProgress): SagaIterator {
  // use cases:
  // -playing channel at live position

  if (!action.meta.liveProgress || !action.meta.progress) {
    return;
  }

  const isLivePosition = yield select(Selectors.isLivePosition);

  if (!isLivePosition) {
    return; // movie, episode, startover/seeked programs, recordings
  }

  const { progress } = action.meta;
  const duration: Seconds = yield select(Selectors.currentDuration);
  if (progress < duration) {
    return;
  }

  if (isLivePosition) {
    yield call(liveRollover);
  }
}

function* concurrentPlaybackActionsSaga(action: PlayerActionTypes): SagaIterator {
  if (yield select(Selectors.isWatchProcessing)) {
    return;
  }

  // runs a race between the request playback operation and the WATCH
  // so, the WATCH will cancel any running operation
  switch (action.type) {
    case ActionType.SEEK_REQUEST:
      yield race([call(seekRequestSaga, action), take(ActionType.WATCH_REQUEST)]);
      break;
    case ActionType.PAUSE_REQUEST:
      yield race([call(pauseRequestSaga), take(ActionType.WATCH_REQUEST)]);
      break;
    case ActionType.RESUME_REQUEST:
      yield race([call(resumeRequestSaga), take(ActionType.WATCH_REQUEST)]);
      break;
    default:
      break;
  }
}

function* parentalRatingValidationSaga(): SagaIterator {
  try {
    while (true) {
      yield delay(Constants.CHECK_PARENTAL_CONTROL_PERIOD);

      const currentAsset: Asset | undefined = yield select(Selectors.currentAsset);

      const assetParentalState = currentAsset ? ParentalControlCore.UI.getAssetParentalState(currentAsset) : undefined;

      const isPlaybackLocked = assetParentalState === ParentalRatingOptionsType.PIN;

      if (isPlaybackLocked) {
        yield put(ActionCreators.setPlaybackLocked(isPlaybackLocked));
      }
    }
  } finally {
    // player paused
  }
}

function takePlaybackState(playbackState: PlaybackState[]): TakeEffect {
  return take((act: AnyAction) => act.type === ActionType.SET_PLAYBACK_STATE && playbackState.indexOf(act.meta) >= 0);
}

function* startParentalRatingValidationSaga(action: SetPlaybackState): SagaIterator {
  const isPlaybackUnlockedByPin: boolean = yield select(Selectors.isPlaybackUnlockedByPin);
  const parentalControl: ParentalControlSettings | undefined = yield select(Ducks.Dms.Selectors.getParentalControl);

  if (action.meta === PlaybackState.PLAYING && !isPlaybackUnlockedByPin && parentalControl?.clientRatingWindow.length) {
    const parentalRatingValidationTask = yield fork(parentalRatingValidationSaga);
    yield takePlaybackState([PlaybackState.PAUSED, PlaybackState.STOP, PlaybackState.PLAYBACK_ERROR]); // because the PLAYBACK_ERROR is triggered first then stops task immediately and don't wait for the STOP
    yield cancel(parentalRatingValidationTask);
  }
}

function* setRecommendationsItem(recommContext: Ducks.Recommendations.RecommContext, name: string): SagaIterator {
  yield put(Ducks.Recommendations.Creators.loadAssets(recommContext));
  const [success] = yield race([
    Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.SUCCESS, recommContext),
    Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.FAIL, recommContext),
  ]);

  if (success) {
    const assets: Asset[] = yield select(Ducks.Recommendations.Selectors.assets, recommContext);
    const assetsWithBadges: Asset[] = yield call(
      RailBadgeEngine.getAssetsWithBadgesListFromRail,
      assets,
      BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.WATCH_NEXT_RAIL
    );

    yield put(
      ActionCreators.setRecommendationsItem({ assets: assetsWithBadges, name, menuItemId: recommContext.menuItemId })
    );
  }
}

function* setRecommendations(upNextFromMenu: MenuItem, currentAssetId: number | undefined): SagaIterator {
  if (upNextFromMenu.type === MenuItemType.EXTERNAL) {
    yield fork(
      setRecommendationsItem,
      {
        menuUiArea: MenuUiArea.UPNEXT,
        menuItemId: upNextFromMenu.id,
        relatedAssetId: currentAssetId,
      },
      upNextFromMenu.name
    );
  }
  for (let index = 0; index < upNextFromMenu?.children.length; index += 1) {
    if (upNextFromMenu.children[index].type === MenuItemType.EXTERNAL) {
      yield fork(
        setRecommendationsItem,
        {
          menuUiArea: MenuUiArea.UPNEXT,
          menuItemId: upNextFromMenu.children[index].id,
          relatedAssetId: currentAssetId,
        },
        upNextFromMenu.children[index].name
      );
    }
  }
}

function* setUpNext(action: WatchSuccess): SagaIterator {
  const isLiveMode: boolean = yield select(Selectors.isLiveMode);
  const isTrailerMode: boolean = yield select(Selectors.isTrailerMode);

  if (isLiveMode || isTrailerMode) {
    return;
  }

  const upNextFromMenu: MenuItem | undefined = yield select(Ducks.Menu.Selectors.getMainMenuItem, MenuUiArea.UPNEXT);
  const hasAgreedToConsent: boolean = yield select(GdprSelectors.hasAgreedToConsent);
  const isRecommendationsEnabled: MenuItem | undefined = yield select(Ducks.Recommendations.Selectors.isFeatureEnabled);

  if (upNextFromMenu && hasAgreedToConsent && isRecommendationsEnabled) {
    yield call(setRecommendations, upNextFromMenu, action.payload.currentAsset?.id);
  }
}

function* unlockPlaybackByPinSaga(): SagaIterator {
  yield put(ParentalControlActions.setParentalLockOccurred(TimeUtils.now()));
}

export function* saga(): SagaIterator {
  yield all([
    takeLatest(ActionType.WATCH_REQUEST, watchRequestSaga),
    takeLatest(ActionType.RESTART_REQUEST, restartRequestSaga), // restart calls SEEK_REQUEST
    takeLeading(
      [ActionType.SEEK_REQUEST, ActionType.PAUSE_REQUEST, ActionType.RESUME_REQUEST],
      concurrentPlaybackActionsSaga
    ),
    takeLatest(ActionType.WATCH_SUCCESS, setUpNext),
    takeLeading(ActionType.SET_STREAM_PROGRESS, progressUpdatedSaga),
    takeLatest(ActionType.SET_PLAYBACK_STATE, startParentalRatingValidationSaga),
    takeLatest(ActionType.LICENSE_REQUEST, ssmLicenseRequestSaga),
    takeLatest(ActionType.UNLOCK_PLAYBACK_BY_PIN, unlockPlaybackByPinSaga),
  ]);
}

export interface ReducerState {
  trickModesEnable: boolean;
  trickModes: TrickModes | undefined;
  playbackState: PlaybackState;
  isPlaybackLocked: boolean;
  isPlaybackUnlockedByPin: boolean;
  isPlaybackAdult: boolean;
  zapping: boolean;
  isTrailerMode: boolean;
  isLiveMode: boolean; // it means that we started the playback playing a channel or ongoing program/recording
  isLivePosition: boolean | undefined; // used in live mode - if 'false' means that the stream was switched to the catchup
  liveProgress: Seconds; // used in live mode
  currentAsset: Asset | undefined;
  relatedChannel: Channel | undefined;
  currentStream: LicensedStream | undefined; // the licensed stream based on the 'originalStream'
  originalStream: Stream | undefined; // original stream from: channel, movie or episode
  targetStreamPosition: Seconds | undefined; // used for seek
  progress: Seconds;
  duration: Seconds;
  isMute: boolean;
  volume: number;
  bitrate: number;
  watch?: AsyncActionState<WatchRequestParams | undefined>;
  restart?: AsyncActionStateNoData;
  seek?: AsyncActionStateNoData;
  pause?: AsyncActionStateNoData;
  resume?: AsyncActionStateNoData;
  recommendations: RecommendationsParams;
  paddingStart?: Seconds;
  paddingEnd?: Seconds;
  nextProgram?: Program | undefined;
  isAutoPlayDenied: boolean;
  playerStats?: boolean;
}

const initialState: ReducerState = {
  playbackState: PlaybackState.NO_STREAM,
  isPlaybackLocked: false,
  isPlaybackUnlockedByPin: false,
  isPlaybackAdult: false,
  trickModes: undefined,
  trickModesEnable: false,
  isTrailerMode: false,
  isLiveMode: false,
  isLivePosition: false,
  liveProgress: 0,
  currentAsset: undefined,
  relatedChannel: undefined,
  currentStream: undefined,
  originalStream: undefined,
  targetStreamPosition: undefined,
  progress: 0,
  duration: 0,
  recommendations: {
    upNext: undefined,
    somethingSimilar: undefined,
    somethingDifferent: undefined,
  },
  isMute: false,
  volume: 100,
  bitrate: 0,
  paddingStart: 0,
  paddingEnd: 0,
  zapping: false,
  isAutoPlayDenied: false,
  playerStats: false,
};

export default function player(state = initialState, action: PlayerActionTypes): ReducerState {
  switch (action.type) {
    case ActionType.SET_PLAYBACK_STATE:
      return {
        ...state,
        playbackState: action.meta,
      };
    case ActionType.SET_PLAYBACK_LOCKED:
      return {
        ...state,
        isPlaybackLocked: action.meta,
      };
    case ActionType.UNLOCK_PLAYBACK_BY_PIN:
      return {
        ...state,
        isPlaybackLocked: false,
        isPlaybackUnlockedByPin: true,
      };
    case ActionType.SET_CURRENT_ASSET:
      return {
        ...state,
        ...action.meta,
      };
    case ActionType.WATCH_REQUEST:
      return {
        ...state,
        watch: { processing: true, processed: false, error: undefined, data: action.meta },
      };
    case ActionType.WATCH_SUCCESS:
      return {
        ...state,
        ...action.payload,
        watch: { processing: false, processed: true, error: undefined, data: undefined },
        nextProgram: undefined,
      };
    case ActionType.WATCH_FAIL:
      return {
        ...state,
        watch: { processing: false, processed: true, error: action.error, data: undefined },
        nextProgram: undefined,
      };
    case ActionType.RESTART_REQUEST:
      return {
        ...state,
        restart: { processing: true, processed: false, error: undefined },
      };
    case ActionType.RESTART_SUCCESS:
      return {
        ...state,
        ...action.payload,
        restart: { processing: false, processed: true, error: undefined },
      };
    case ActionType.RESTART_FAIL:
      return {
        ...state,
        restart: { processing: false, processed: true, error: action.error },
      };
    case ActionType.SEEK_REQUEST:
      return {
        ...state,
        seek: { processing: true, processed: false, error: undefined },
      };
    case ActionType.SEEK_SUCCESS:
      return {
        ...state,
        ...action.payload,
        seek: { processing: false, processed: true, error: undefined },
      };
    case ActionType.SEEK_FAIL:
      return {
        ...state,
        seek: { processing: false, processed: true, error: action.error },
      };
    case ActionType.PAUSE_REQUEST:
      return {
        ...state,
        pause: { processing: true, processed: false, error: undefined },
      };
    case ActionType.PAUSE_SUCCESS:
      return {
        ...state,
        pause: { processing: false, processed: true, error: undefined },
      };
    case ActionType.PAUSE_FAIL:
      return {
        ...state,
        pause: { processing: false, processed: true, error: action.error },
      };
    case ActionType.RESUME_REQUEST:
      return {
        ...state,
        resume: { processing: true, processed: false, error: undefined },
      };
    case ActionType.RESUME_SUCCESS:
      return {
        ...state,
        ...action.payload,
        resume: { processing: false, processed: true, error: undefined },
      };
    case ActionType.RESUME_FAIL:
      return {
        ...state,
        resume: { processing: false, processed: true, error: action.error },
      };
    case ActionType.SET_STREAM_PROGRESS:
      return {
        ...state,
        progress: action.meta?.progress ? action.meta.progress : state.progress,
        liveProgress: action.meta?.liveProgress ? action.meta.liveProgress : state.liveProgress,
      };
    case ActionType.SET_STREAM_DURATION:
      return {
        ...state,
        duration: action.meta,
      };
    case ActionType.SET_RECOMMENDATIONS_ITEM: {
      const { assets, menuItemId } = action.payload;
      return {
        ...state,
        recommendations: {
          ...state.recommendations,
          [action.payload.name]: { assets, menuItemId },
        },
      };
    }
    case ActionType.SET_MUTE_STATE:
      return {
        ...state,
        isMute: action.meta,
      };
    case ActionType.SET_VOLUME:
      return {
        ...state,
        volume: action.meta,
      };
    case ActionType.SET_BITRATE:
      return {
        ...state,
        bitrate: action.meta,
      };
    case ActionType.SET_NEXT_PROGRAM:
      return {
        ...state,
        nextProgram: action.payload,
      };
    case ActionType.SET_AUTO_PLAY_DENIED: {
      return {
        ...state,
        isAutoPlayDenied: action.meta,
      };
    }
    case ActionType.CLEAR_STATE:
      return initialState;
    case ActionType.ACTIVATE_PLAYER_STATS: {
      return {
        ...state,
        playerStats: action.meta,
      };
    }
    default:
      return state;
  }
}
